"use client";

import { useState } from "react";
import { DataTable, TextField } from "@/components/tool-kit/primitives";
import { CARD, WIDGET_H2 } from "@/components/tool-kit/styles";
import ExactConverter, {
  type ExactUnit,
} from "@/components/tools/calendars/exact-converter";
import { useToolUi } from "@/components/tools/tool-ui";
import {
  engineeringPrefix,
  prefixFactor,
  SI_PREFIXES,
} from "@/lib/tool-kit/calendars/prefixes";

const SUPERSCRIPTS = "⁰¹²³⁴⁵⁶⁷⁸⁹";
const DIGIT = /\d/g;

function power(exponent: number): string {
  const digits = String(Math.abs(exponent)).replace(
    DIGIT,
    (digit) => SUPERSCRIPTS[Number(digit)] ?? digit
  );
  return `10${exponent < 0 ? "⁻" : ""}${digits}`;
}

/** English strings. A translated content file overrides these through `ui`. */
export const METRIC_PREFIX_CONVERTER_STRINGS = {
  valueLabel: "Value",
  valueHint:
    "A number such as 4.7, or scientific notation such as 1.5e-9 or 2.2 × 10^6.",
  unitLabel: "Unit symbol (optional)",
  unitHint:
    "Any SI unit: m, g, s, Pa, W, Hz… The prefix is put in front of it.",
  unitPlaceholder: "m",
  fromLabel: "From prefix",
  toLabel: "To prefix",
  digitsLabel: "Significant digits",
  swap: "Swap prefixes",
  resultHeading: "Result",
  plainLabel: "Converted value",
  scientificLabel: "Scientific notation",
  bestLabel: "Closest engineering prefix",
  factorLabel: "Conversion factor",
  factorLine: "1 {from} = {factor} {to}",
  exact: "Exact: a prefix is an exact power of ten, so nothing was rounded.",
  rounded: "Rounded to {digits} significant digits.",
  tableHeading: "Your value in every prefix",
  unitColumn: "Prefix",
  valueColumn: "Value",
  enterPrompt: "Enter a number to convert.",
  invalidNumber:
    "That is not a number. Use digits with an optional decimal point and exponent, for example 4.7 or 1.5e-9.",
  copy: "Copy result",
  copied: "Copied!",
  copySummary: "{value} {from} = {result} {to}",
  unitNames: {
    quetta: "quetta (Q), 10³⁰",
    ronna: "ronna (R), 10²⁷",
    yotta: "yotta (Y), 10²⁴",
    zetta: "zetta (Z), 10²¹",
    exa: "exa (E), 10¹⁸",
    peta: "peta (P), 10¹⁵",
    tera: "tera (T), 10¹²",
    giga: "giga (G), 10⁹",
    mega: "mega (M), 10⁶",
    kilo: "kilo (k), 10³",
    hecto: "hecto (h), 10²",
    deca: "deca (da), 10¹",
    none: "no prefix, 10⁰",
    deci: "deci (d), 10⁻¹",
    centi: "centi (c), 10⁻²",
    milli: "milli (m), 10⁻³",
    micro: "micro (µ), 10⁻⁶",
    nano: "nano (n), 10⁻⁹",
    pico: "pico (p), 10⁻¹²",
    femto: "femto (f), 10⁻¹⁵",
    atto: "atto (a), 10⁻¹⁸",
    zepto: "zepto (z), 10⁻²¹",
    yocto: "yocto (y), 10⁻²⁴",
    ronto: "ronto (r), 10⁻²⁷",
    quecto: "quecto (q), 10⁻³⁰",
  },
  prefixNames: {
    quetta: "quetta",
    ronna: "ronna",
    yotta: "yotta",
    zetta: "zetta",
    exa: "exa",
    peta: "peta",
    tera: "tera",
    giga: "giga",
    mega: "mega",
    kilo: "kilo",
    hecto: "hecto",
    deca: "deca",
    none: "no prefix",
    deci: "deci",
    centi: "centi",
    milli: "milli",
    micro: "micro",
    nano: "nano",
    pico: "pico",
    femto: "femto",
    atto: "atto",
    zepto: "zepto",
    yocto: "yocto",
    ronto: "ronto",
    quecto: "quecto",
  },
  referenceHeading: "The SI prefixes",
  prefixColumn: "Prefix",
  symbolColumn: "Symbol",
  powerColumn: "Factor",
  addedColumn: "Added",
  gramNote:
    "Prefixes attach to the gram, not the kilogram: a milligram is 10⁻³ g, and the kilogram is itself 10³ g. Binary prefixes (kibi, mebi, gibi) are a different system for computer storage.",
} as const;

export default function MetricPrefixConverterWidget() {
  const strings = useToolUi(METRIC_PREFIX_CONVERTER_STRINGS);
  const [unit, setUnit] = useState("m");
  const trimmed = unit.trim();
  const units: ExactUnit[] = SI_PREFIXES.map((prefix) => ({
    id: prefix.id,
    symbol: `${prefix.symbol}${trimmed}` || "1",
    factor: prefixFactor(prefix),
  }));
  return (
    <div className="space-y-6">
      <div className="max-w-xs">
        <TextField
          hint={strings.unitHint}
          id="mp-unit"
          label={strings.unitLabel}
          onChange={setUnit}
          placeholder={strings.unitPlaceholder}
          value={unit}
        />
      </div>
      <ExactConverter
        defaults={{ value: "4.7", from: "kilo", to: "none" }}
        idPrefix="mp"
        notes={[strings.gramNote]}
        strings={strings}
        suggest={(exponent) => engineeringPrefix(exponent).id}
        units={units}
      />
      <section className={`${CARD} space-y-3`}>
        <h2 className={WIDGET_H2}>{strings.referenceHeading}</h2>
        <DataTable
          head={[
            strings.prefixColumn,
            strings.symbolColumn,
            strings.powerColumn,
            strings.addedColumn,
          ]}
          rows={SI_PREFIXES.filter((prefix) => prefix.id !== "none").map(
            (prefix) => [
              strings.prefixNames[
                prefix.id as keyof typeof strings.prefixNames
              ] ?? prefix.id,
              prefix.symbol,
              power(prefix.exponent),
              prefix.addedIn ? String(prefix.addedIn) : "",
            ]
          )}
        />
      </section>
    </div>
  );
}
